import {
  CASHBACK_ENTRY_TYPE_LABELS,
  formatCurrency,
  formatDate,
  fromDateInput,
  startOfMonth,
  toDateInput,
} from '@crm/shared';
import { Badge, Card, CardContent, cn, Input, Select, Spinner } from '@crm/ui';
import { ArrowDownLeft, ArrowUpRight, Coins, TimerOff, Wallet } from 'lucide-react';
import { useState } from 'react';
import { ExportPdfButton, PageHeader, Pagination, PrintButton, StatCard } from '@/components';
import { useDocumentTitle } from '@/hooks';
import { toErrorMessage } from '@/modules/auth/utils';
import { useCashbackEntries } from '../api';

/** Extrato de cashback: o que foi creditado, o que foi usado e o que sobrou. */
export function CashbackEntriesPage() {
  useDocumentTitle('Extrato de cashback');

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [type, setType] = useState<'all' | 'EARN' | 'REDEEM'>('all');
  const [from, setFrom] = useState(() => toDateInput(startOfMonth(new Date())));
  const [to, setTo] = useState(() => toDateInput(new Date()));

  const entries = useCashbackEntries({
    page,
    perPage,
    type: type === 'all' ? undefined : type,
    from: from ? fromDateInput(from).toISOString() : undefined,
    to: to ? fromDateInput(to, true).toISOString() : undefined,
  });

  const totals = entries.data?.totals;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Coins />}
        title="Extrato de cashback"
        description="Creditos gerados nos atendimentos e usados como desconto."
        actions={
          <div className="flex items-center gap-2">
            <PrintButton />
            <ExportPdfButton title="Extrato de cashback" />
          </div>
        }
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 sm:gap-4">
        <StatCard
          title="Creditado"
          value={formatCurrency(totals?.earned ?? 0)}
          hint="No periodo escolhido"
          icon={<ArrowUpRight />}
        />
        <StatCard
          title="Usado"
          value={formatCurrency(totals?.used ?? 0)}
          hint="Abatido em atendimentos"
          icon={<ArrowDownLeft />}
        />
        <StatCard
          title="Saldo a usar"
          value={formatCurrency(totals?.active ?? 0)}
          hint="Creditos do periodo ainda validos"
          icon={<Wallet />}
        />
        <StatCard
          title="Vencido"
          value={formatCurrency(totals?.expired ?? 0)}
          hint="Creditos que perderam a validade"
          icon={<TimerOff />}
        />
      </div>

      <Card variant="flush">
        <CardContent className="space-y-6">
          <div className="grid gap-3 sm:grid-cols-2">
            <Select
              aria-label="Tipo de lancamento"
              value={type}
              onChange={(event) => {
                setType(event.target.value as 'all' | 'EARN' | 'REDEEM');
                setPage(1);
              }}
            >
              <option value="all">Creditos e usos</option>
              <option value="EARN">So creditos</option>
              <option value="REDEEM">So usos</option>
            </Select>

            <div className="flex items-center gap-2">
              <Input
                type="date"
                aria-label="De"
                value={from}
                onChange={(event) => {
                  setFrom(event.target.value);
                  setPage(1);
                }}
              />
              <span className="text-sm text-muted-foreground">a</span>
              <Input
                type="date"
                aria-label="Ate"
                value={to}
                onChange={(event) => {
                  setTo(event.target.value);
                  setPage(1);
                }}
              />
            </div>
          </div>

          {entries.isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner className="h-6 w-6 text-muted-foreground" />
            </div>
          ) : entries.isError ? (
            <p className="text-sm text-destructive">{toErrorMessage(entries.error)}</p>
          ) : entries.data && entries.data.data.length > 0 ? (
            <ul className="divide-y divide-border">
              {entries.data.data.map((entry) => (
                <li
                  key={entry.id}
                  className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    {entry.customerImage ? (
                      <img
                        src={entry.customerImage}
                        alt={entry.customerName}
                        className="h-9 w-9 shrink-0 rounded-full border border-border object-cover"
                      />
                    ) : null}
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium">{entry.customerName}</p>
                        <Badge variant={entry.type === 'EARN' ? 'secondary' : 'outline'}>
                          {CASHBACK_ENTRY_TYPE_LABELS[entry.type]}
                        </Badge>
                        {entry.isExpired ? <Badge variant="destructive">Vencido</Badge> : null}
                      </div>
                      <p className="truncate text-xs text-muted-foreground">
                        {formatDate(entry.createdAt)}
                        {entry.type === 'EARN' && entry.expiresAt
                          ? ` - vale ate ${formatDate(entry.expiresAt)}`
                          : ''}
                        {entry.type === 'EARN' && entry.usedAmount > 0
                          ? ` - ${formatCurrency(entry.usedAmount)} ja usado`
                          : ''}
                      </p>
                    </div>
                  </div>

                  <p
                    className={cn(
                      'shrink-0 font-semibold tabular-nums',
                      entry.type === 'EARN' ? 'text-emerald-600' : 'text-muted-foreground',
                    )}
                  >
                    {entry.type === 'EARN' ? '+' : '-'}
                    {formatCurrency(entry.amount)}
                  </p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="py-6 text-center text-sm text-muted-foreground">
              Nenhum lancamento de cashback no periodo.
            </p>
          )}

          <Pagination
            meta={entries.data?.meta}
            label="lancamentos"
            onChange={setPage}
            perPage={perPage}
            onPerPageChange={(value) => {
              setPerPage(value);
              setPage(1);
            }}
          />
        </CardContent>
      </Card>
    </div>
  );
}
